import * as Constants from '../constants/action-constants';

import { PageResponse, PaginationParams, Request } from 'vrlcm-gui-types';
import {
  PollRequestDetailsStart,
  PollRequestsStart,
  SubmitRetryRequest,
  clearRetryRequestFailedStatus,
  pollRequestDetails,
  pollRequestDetailsFailed,
  pollRequestDetailsSuccess,
  pollRequests,
  pollRequestDetailsStop,
  pollRequestsFailed,
  pollRequestsSuccess,
  submitRetryRequestFailed,
  submitRetryRequestSuccess
} from '../actions/Requests';
import {
  all,
  call,
  delay,
  put,
  race,
  take,
  takeLatest
} from 'redux-saga/effects';

import Backoff from 'backo';
import { ROUTES } from '../constants/RouteConstants';
import { RequestApi } from 'vrlcm-gui-api';
import { push } from 'connected-react-router';

function* fetchRequests(paginationParams: PaginationParams) {
  const backoff = new Backoff({ min: 5000, max: 60000 });
  while (true) {
    try {
      yield put(pollRequests());
      const requests: PageResponse<Request> = yield call(
        RequestApi.getRequests,
        paginationParams
      );
      yield put(pollRequestsSuccess(requests));
      backoff.reset();
      yield delay(15000);
    } catch (error) {
      yield put(pollRequestsFailed());
      yield delay(backoff.duration());
    }
  }
}

function* pollForRequests() {
  while (true) {
    const action: PollRequestsStart = yield take(Constants.POLL_REQUESTS_START);
    yield race({
      poll: call(fetchRequests, action.payload.paginationParams),
      stop: take(Constants.POLL_REQUESTS_STOP)
    });
  }
}

function* pollRequestDetailsWorker(id: string, isRequestChart: boolean) {
  const backoff = new Backoff({ min: 5000, max: 60000 });
  const backoffFetch = new Backoff({ min: 1000, max: isRequestChart ? 4000 : 15000 });
  while (true) {
    try {
      yield put(pollRequestDetails(id));
      const request: Request = yield call(RequestApi.getRequest, id);
      yield put(pollRequestDetailsSuccess(request));
      if (request && (request.state === 'FAILED' || request.state === 'COMPLETED')) {
        yield put(pollRequestDetailsStop());
      } else {
        backoff.reset();
        yield delay(backoffFetch.duration());
      }
    } catch (error) {
      yield put(pollRequestDetailsFailed());
      yield delay(backoff.duration());
    }
  }
}

function* watchPollRequestDetailsStart() {
  while (true) {
    const action: PollRequestDetailsStart = yield take(
      Constants.POLL_REQUEST_DETAILS_START
    );
    yield race({
      poll: call(pollRequestDetailsWorker, action.payload.id, action.payload.isRequestChart),
      stop: take(Constants.POLL_REQUEST_DETAILS_STOP)
    });
  }
}

function* submitRetryRequestHandler(action: SubmitRetryRequest) {
  try {
    yield call(
      RequestApi.submitRetryRequest,
      action.payload.requestId,
      action.payload.causeList
    );
    yield put(submitRetryRequestSuccess());
    if (action.payload.absoluteRedirection) {
      window.location.href = ROUTES.REQUESTS;
    } else {
      yield put(push(ROUTES.REQUESTS));
    }
  } catch (error) {
    yield put(submitRetryRequestFailed());
    yield delay(3000);
    yield put(clearRetryRequestFailedStatus());
  }
}

function* watchForSubmitRetryRequest() {
  yield takeLatest(Constants.SUBMIT_RETRY_REQUEST, submitRetryRequestHandler);
}

export default function* requestsSaga() {
  yield all([
    watchPollRequestDetailsStart(),
    pollForRequests(),
    watchForSubmitRetryRequest()
  ]);
}
